import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useBankID } from '../../hooks/useBankID';
import '../../scss/bank-id-auth.scss';
import { bankidcolor, CloseIcon, ReloadIcon, externalLinkBlack, Softwareqr } from '../images/svg/icons';
import { Loadinggif } from '../images/gif/icons';
import ReactivateModal from '../reactivatemodal/reactivate.modal';
import { getItem, setItem } from '../../helper/localstorage.helper';
import { selectSsnMismatch, selectSsnUpdatePending, selectSsnUpdateRejected, selectSsnRejectionReason, selectNameMismatchType } from '../../features/bankid/bankid.selectors';
import { setSsnMismatch, setSsnUpdatePending, setSsnUpdateRejected, setSsnRejectionReason, setNameMismatchType, submitSsnUpdate } from '../../features/bankid/bankid.slice';
import { toast } from 'react-toastify';
import UIInput from '../ui-input';
import { blobURL } from '../../../common/StringUtils';
import { LOCALES } from '../../../common/constants';

export interface BankIDComponentProps {
    mode: 'login' | 'signup';
    accountType?: 'freelancer' | 'business';
    onSuccess?: (userData: any) => void;
    onError?: (error: string) => void;
    onClose?: () => void;
    title?: string;
    className?: string;
    showCloseButton?: boolean;
    autoInitialize?: boolean;
    hideOptions?: boolean;
    footerContent?: React.ReactNode;
    subHeaderContent?: React.ReactNode;
    collectMode?: 'default' | 'verification';
    headerLeftContent?: React.ReactNode;
    skipSameDevicePolling?: boolean;
    autoReloadOnExpiry?: boolean;
}

/**
 * Unified BankID Component that follows Redux patterns
 * This component can be used for both login and signup flows
 */
const BankIDComponent: React.FC<BankIDComponentProps> = ({
    mode,
    accountType,
    onSuccess,
    onError,
    onClose,
    title,
    className = '',
    showCloseButton = true,
    autoInitialize = true,
    hideOptions = false,
    footerContent,
    subHeaderContent,
    collectMode,
    headerLeftContent,
    skipSameDevicePolling = false,
    autoReloadOnExpiry = false,
}) => {
    const { t, i18n } = useTranslation();

    // Scroll-aware up/down controls for the login-theme overlay modal (scrollbar hidden).
    // Show "up" only when scrolled down, "down" only when more is below, both in the
    // middle, and neither when the content fits (nothing to scroll).
    const modalScrollRef = useRef<HTMLDivElement>(null);
    const [scrollNav, setScrollNav] = useState({ up: false, down: false });
    const isLoginTheme = className?.includes('login-theme');
    useEffect(() => {
        if (!isLoginTheme) return;
        const el = modalScrollRef.current;
        if (!el) return;
        const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const maxScroll = scrollHeight - clientHeight;
            setScrollNav({
                up: scrollTop > 8,
                down: maxScroll > 8 && scrollTop < maxScroll - 8,
            });
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild); // content height changes
        return () => {
            el.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [isLoginTheme]);
    const scrollModalBy = (delta: number) =>
        modalScrollRef.current?.scrollBy({ top: delta, behavior: 'smooth' });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ssnMismatch = useSelector(selectSsnMismatch);
    const ssnUpdatePending = useSelector(selectSsnUpdatePending);
    const ssnUpdateRejected = useSelector(selectSsnUpdateRejected);
    const ssnRejectionReason = useSelector(selectSsnRejectionReason);
    const nameMismatchType = useSelector(selectNameMismatchType);
    const [ssnValue, setSsnValue] = useState('');
    const [ssnError, setSsnError] = useState('');
    const [isSsnSubmitting, setIsSsnSubmitting] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [showSignupEmailStep, setShowSignupEmailStep] = useState(false);
    const [pendingMethod, setPendingMethod] = useState<'same-device' | 'qr-code' | null>(null);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [termsError, setTermsError] = useState('');
    const currentLanguage = new URLSearchParams(location.search).get('lang') || 'sv-se';
    const invoicePDF = `${blobURL}/invoice.pdf`;
    const invoiceSwidishPDF = `${blobURL}/invoiceSwidish.pdf`;
    const workAgrementSwedish = `${blobURL}/Allmänt avtal för Egenanställning.pdf`;
    const workAgrementEnglish = `${blobURL}/General Contract for Self-Employment.pdf`;


    const {
        isInitializing,
        isQRModalOpen,
        qrState,
        qrCodeData,
        isAuthSuccessful,
        needsReload,
        sessionTimeout,
        canUseSameDevice,
        canUseQRCode,
        toggleReactivate,
        inactiveAccount,
        userData,
        error,
        initializeQRFlow,
        authenticateSameDevice,
        handleQRReload,
        closeQRModal,
        getTitle,
        handleReactivationClose,
        handleReactivationSuccess,
    } = useBankID({
        mode,
        accountType,
        onSuccess,
        onError,
        autoInitialize,
        collectMode,
        skipSameDevicePolling,
    });

    // Auto-reload the QR code when it expires, instead of showing the error screen.
    // Only active when autoReloadOnExpiry=true (signup/signin inline). Other pages
    // (e.g. create-invoice verification modal) are unaffected.
    React.useEffect(() => {
        if (autoReloadOnExpiry && needsReload && sessionTimeout) {
            handleQRReload();
        }
    }, [autoReloadOnExpiry, needsReload, sessionTimeout, handleQRReload]);

    const handleSsnModalClose = () => {
        dispatch(setSsnMismatch(false));
        dispatch(setSsnUpdatePending(false));
        dispatch(setSsnUpdateRejected(false));
        dispatch(setSsnRejectionReason(null));
        setSsnValue('');
        setSsnError('');
    };

    const handleSsnSubmit = async () => {
        const trimmed = ssnValue.trim();
        if (!/^\d{12}$/.test(trimmed)) {
            setSsnError(t('SSN_VALIDATION_12_DIGITS'));
            return;
        }
        if (trimmed.slice(-4) === '0000') {
            setSsnError(t('SSN_VALIDATION_LAST_4'));
            return;
        }
        setSsnError('');
        setIsSsnSubmitting(true);
        const result = await (dispatch as any)(submitSsnUpdate({ newSsn: trimmed }));
        setIsSsnSubmitting(false);
        setSsnValue('');
        if (submitSsnUpdate.rejected.match(result)) {
            const payload = result.payload as any;
            if (payload?.isDuplicate) {
                toast.error(t('SSN_DUPLICATE_ERROR') as string);
            } else if (payload?.isSameSsn) {
                toast.error(t('SSN_SAME_AS_CURRENT_ERROR') as string);
            }
        }
    };

    const handleBankIDAuth = async (method: 'same-device' | 'qr-code') => {
        if (mode === 'signup') {
            setPendingMethod(method);
            setShowSignupEmailStep(true);
            return;
        }
        if (method === 'qr-code') {
            await initializeQRFlow();
        } else if (method === 'same-device') {
            await authenticateSameDevice();
            // When skipSameDevicePolling=true (signup page), auth is handled by
            // the BankID redirect URL on the backend — no frontend polling needed.
            // Clearing qrCodeData.id here prevents the polling useEffect from firing.
            if (skipSameDevicePolling) {
                closeQRModal();
            }
        }
    };

    const handleSignupEmailContinue = async () => {
        const trimmed = email.trim();
        const tduid = getItem("tduid") ?? ""
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setEmailError(t('INVALID_EMAIL_FORMAT') || 'Please enter a valid email address');
            return;
        }
        if (!agreeToTerms) {
            setTermsError(t('PLEASE_SELECT_TERMS_AND_CONDITION'));
            return;
        }

        setEmailError('');
        setTermsError('');
        localStorage.setItem('SignUpemail', trimmed);
        setShowSignupEmailStep(false);
        setPendingMethod(null);
        if (pendingMethod === 'same-device') {
            await authenticateSameDevice(trimmed,tduid);
            if (skipSameDevicePolling) {
                closeQRModal();
            }
        } else {
            await initializeQRFlow(trimmed);
        }
    };

    const handleCloseQRModal = () => {
        setShowSignupEmailStep(false);
        setEmail('');
        setEmailError('');
        setPendingMethod(null);
        closeQRModal();
    };

    const handleBackFromQR = () => {
        setShowSignupEmailStep(false);
        setEmail('');
        setEmailError('');
        setPendingMethod(null);
        closeQRModal();
    };

    const handleBackFromEmailStep = () => {
        setShowSignupEmailStep(false);
        setEmail('');
        setEmailError('');
        setTermsError('');
        setAgreeToTerms(false);
        setPendingMethod(null);
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };
    const userEmail = userData?.email || getItem('userEmail') || '';

    return (
        <div className={`bankid-auth-overlay ${className}`}>
            <div className="bankid-auth-modal" ref={modalScrollRef}>
                <div className="bankid-content border-b-2">
                    <div className='d-flex justify-content-between align-items-center' style={{ minHeight: '32px' }}>
                        <div className='d-flex align-items-center' style={{ gap: '8px', flex: 1 }}>
                            {!isQRModalOpen && !ssnMismatch && !nameMismatchType && headerLeftContent}
                            <h2 className="bankid-title mb-2" style={{ margin: 0 }}>
                                {ssnMismatch
                                    ? t('BANKID_SSN_MISMATCH_TITLE')
                                    : nameMismatchType === 'firstName'
                                        ? t('BANKID_FIRSTNAME_MISMATCH_TITLE')
                                        : nameMismatchType === 'lastName'
                                            ? t('BANKID_LASTNAME_MISMATCH_TITLE')
                                            : (title || getTitle())}
                            </h2>
                        </div>
                        {showCloseButton && onClose && (
                            <button
                                onClick={handleClose}
                                className="close-btn d-flex pt-0"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                            >
                                <img src={CloseIcon} alt="Close" height={16} width={16} />
                            </button>
                        )}
                    </div>

                    <div style={{ borderTop: '1px solid #E9EAEB' }} />

                    {subHeaderContent && !isQRModalOpen && !ssnMismatch && !nameMismatchType && (
                        <div className="bankid-subheader-content">
                            {subHeaderContent}
                        </div>
                    )}

                    {/* ── SSN mismatch inline (inside the BankID modal) ── */}
                    {ssnMismatch && (
                        <>
                            <div className="ssn-mismatch-body">
                                {ssnUpdatePending ? (
                                    <div className="ssn-pending-card">
                                        <div className="ssn-pending-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="#6750b8" strokeWidth="1.8" fill="#f3f0fd"/>
                                                <path d="M12 7v5l3 3" stroke="#6750b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <h3 className="ssn-pending-title">{t('SSN_UPDATE_PENDING_TITLE')}</h3>
                                        <p className="ssn-pending-message">{t('SSN_UPDATE_PENDING_MESSAGE')}</p>
                                        <p className="ssn-pending-description">{t('SSN_UPDATE_PENDING_DESCRIPTION')}</p>
                                    </div>
                                ) : (
                                    <>
                                        {ssnUpdateRejected && (
                                            <div className="ssn-rejected-banner">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#ef4444"/>
                                                </svg>
                                                <div>
                                                    <span>{t('SSN_UPDATE_REJECTED_MESSAGE')}</span>
                                                    {ssnRejectionReason && (
                                                        <p className="ssn-rejected-reason">{t('SSN_REJECTION_REASON_LABEL')}: {ssnRejectionReason}</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <p className="ssn-mismatch-message">{t('BANKID_SSN_MISMATCH_MESSAGE')}</p>
                                        <p className="ssn-mismatch-description">{t('BANKID_SSN_MISMATCH_DESCRIPTION')}</p>
                                        <div className="ssn-mismatch-field">
                                            <label className="ssn-mismatch-label" htmlFor="ssn-mismatch-input">
                                                {t('SSN_INPUT_LABEL')}
                                            </label>
                                            <input
                                                id="ssn-mismatch-input"
                                                className={`ssn-mismatch-input${ssnError ? ' ssn-mismatch-input--error' : ''}`}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={12}
                                                placeholder={t('SSN_INPUT_PLACEHOLDER')}
                                                value={ssnValue}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                                                    setSsnValue(val);
                                                    if (ssnError) setSsnError('');
                                                }}
                                                disabled={isSsnSubmitting}
                                            />
                                            {ssnError && <p className="ssn-mismatch-error">{ssnError}</p>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="ssn-mismatch-footer">
                                {ssnUpdatePending ? (
                                    <button
                                        className="ssn-mismatch-submit"
                                        style={{ flex: 1 }}
                                        onClick={handleSsnModalClose}
                                    >
                                        {t('SSN_UPDATE_PENDING_DISMISS')}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="ssn-mismatch-cancel"
                                            onClick={handleSsnModalClose}
                                            disabled={isSsnSubmitting}
                                        >
                                            {t('SSN_CANCEL_BUTTON')}
                                        </button>
                                        <button
                                            className="ssn-mismatch-submit"
                                            onClick={handleSsnSubmit}
                                            disabled={isSsnSubmitting || ssnValue.length !== 12}
                                        >
                                            {isSsnSubmitting ? (
                                                <span className="ssn-mismatch-spinner" />
                                            ) : (
                                                t('SSN_SUBMIT_BUTTON')
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {/* ── Name mismatch inline (inside the BankID modal) ── */}
                    {nameMismatchType && (
                        <>
                            <div className="ssn-mismatch-body">
                                <p className="ssn-mismatch-message">
                                    {nameMismatchType === 'firstName'
                                        ? t('BANKID_FIRSTNAME_MISMATCH_MESSAGE')
                                        : t('BANKID_LASTNAME_MISMATCH_MESSAGE')}
                                </p>
                                <p className="ssn-mismatch-description">
                                    {nameMismatchType === 'firstName'
                                        ? t('BANKID_FIRSTNAME_MISMATCH_DESCRIPTION')
                                        : t('BANKID_LASTNAME_MISMATCH_DESCRIPTION')}
                                </p>
                            </div>
                            <div className="ssn-mismatch-footer">
                                <button
                                    className="ssn-mismatch-cancel"
                                    onClick={() => {
                                        closeQRModal();
                                        dispatch(setNameMismatchType(null));
                                    }}
                                >
                                    {t('CANCEL')}
                                </button>
                                <button
                                    className="ssn-mismatch-submit"
                                    onClick={() => {
                                        closeQRModal();
                                        dispatch(setNameMismatchType(null));
                                        navigate(`/profile-settings?lang=${i18n.language}`);
                                    }}
                                >
                                    {t('GO')}
                                </button>
                            </div>
                        </>
                    )}

                    {/* ── Signup email collection step (before QR is initialised) ── */}
                    {!ssnMismatch && !nameMismatchType && showSignupEmailStep && !isQRModalOpen && (
                        <div className="bankid-inline-qr">
                            <div className="bankid-instruction-row" style={{ marginBottom: '16px' }}>
                                <button className="bankid-back-btn" onClick={handleBackFromEmailStep}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                <div className="bankid-instruction-content">
                                    <div className="bankid-icon">
                                        <img src={bankidcolor} width={16} height={16} alt="" />
                                    </div>
                                    <span className="bankid-instruction-text">{t('SCAN_QR_FROM_BANK_APP')}</span>
                                </div>
                            </div>
                            <div className="ssn-mismatch-field" style={{ marginTop: '8px' }}>
                                <label className="ssn-mismatch-label" htmlFor="signup-email-input">
                                    {t('EMAIL') || 'Email'}
                                </label>
                                <UIInput
                                    id="signup-email-input"
                                    className={`ssn-mismatch-input${emailError ? ' ssn-mismatch-input--error' : ''}`}
                                    type="email"
                                    placeholder={t('ENTER_EMAIL') || 'Enter your email address'}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (emailError) setEmailError('');
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSignupEmailContinue(); }}
                                    autoFocus
                                />
                                {emailError && <p className="ssn-mismatch-error">{emailError}</p>}
                            </div>
                            <div className="checkbox-wrapper pt-3">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="checkbox-input"
                                        checked={agreeToTerms}
                                        onChange={(e) => {
                                            setAgreeToTerms(e.target.checked);
                                            if (e.target.checked) setTermsError('');
                                        }}
                                    />
                                    <span className="checkbox-checkmark"></span>
                                    <span className="checkbox-text">
                                        {t("REGISTRATION_TERMS_TEXT_1")}

                                        <Link
                                            to={currentLanguage === LOCALES.EN_US
                                                ? workAgrementEnglish
                                                : workAgrementSwedish
                                            }
                                            target="_blank"
                                        >
                                            {t("REGISTRATION_TERMS_LINK_1")}
                                        </Link>
                                        {t("REGISTRATION_TERMS_TEXT_2")}
                                        <Link
                                            to={currentLanguage === LOCALES.EN_US
                                                ? invoicePDF
                                                : invoiceSwidishPDF
                                            }
                                            target="_blank"
                                        >
                                            {t("REGISTRATION_TERMS_LINK_2")}</Link>
                                        {t("REGISTRATION_TERMS_TEXT_3")}
                                    </span>
                                </label>
                            </div>
                            {termsError && (
                                <div className="error-message pt-3 " style={{ color: 'red' }}>
                                    {termsError}
                                </div>
                            )}
                            <div className="ssn-mismatch-footer" style={{ marginTop: '16px' }}>
                                <button
                                    className="ssn-mismatch-submit"
                                    style={{ flex: 1 }}
                                    onClick={handleSignupEmailContinue}
                                    disabled={!email.trim()}
                                >
                                    {t('CONTINUE') || 'Continue'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Inline QR view replaces the option buttons when QR flow is active ── */}
                    {!ssnMismatch && !nameMismatchType && !showSignupEmailStep && (isQRModalOpen ? (
                        <div className="bankid-inline-qr">
                            <div className="bankid-instruction-row" style={{ marginBottom: '16px' }}>
                                <button className="bankid-back-btn" onClick={handleCloseQRModal}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                <div className="bankid-instruction-content">
                                    <div className="bankid-icon">
                                        <img src={bankidcolor} width={16} height={16} alt="" />
                                    </div>
                                    <span className="bankid-instruction-text">{t('SCAN_QR_FROM_BANK_APP')}</span>
                                </div>
                            </div>

                            <div className="bankid-qr-container">
                                {qrState.isLoading || (autoReloadOnExpiry && needsReload && sessionTimeout) ? (
                                    <img src={Loadinggif} width={200} alt="Loading..." />
                                ) : needsReload ? (
                                    <div className="d-flex justify-content-center align-items-center flex-column gap-3">
                                        <div className="mt-4">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" fill="none">
                                                <path d="M49.99 0C22.4131 0 0 22.41 0 49.988C0 77.566 22.4171 100 49.99 100C77.5669 100 100 77.566 100 49.988C100 22.41 77.5669 0 49.99 0ZM49.99 6.25C74.188 6.25 93.7462 25.789 93.7462 49.988C93.7462 74.187 74.188 93.75 49.99 93.75C25.792 93.75 6.25375 74.188 6.25375 49.988C6.25375 25.788 25.792 6.25 49.99 6.25ZM37.4865 34.367C36.6623 34.3787 35.8733 34.7108 35.2952 35.2967C34.0804 36.5155 34.0804 38.4842 35.2952 39.7029L45.5838 49.9999L35.2952 60.3049C34.0804 61.5237 34.0804 63.4924 35.2952 64.7111C36.5139 65.922 38.4826 65.922 39.7012 64.7111L49.9898 54.4181L60.2744 64.7111C60.8603 65.3009 61.6571 65.6291 62.4891 65.6291C63.3211 65.6291 64.1179 65.3009 64.7038 64.7111C65.9186 63.4923 65.9186 61.5236 64.7038 60.3049L54.4032 49.9999L64.7038 39.7029C65.9186 38.4841 65.9186 36.5154 64.7038 35.2967C64.1179 34.7029 63.3211 34.3709 62.4891 34.367C61.6571 34.3709 60.8603 34.7029 60.2744 35.2967L49.9898 45.5857L39.7012 35.2967C39.1153 34.7029 38.3185 34.3709 37.4865 34.367Z" fill="#F04438" />
                                            </svg>
                                        </div>
                                        <h6 className="text-red my-2">{t(qrState.customMessage)}</h6>
                                    </div>
) : qrCodeData?.qrCode ? (
    <div className="bankid-qr-code">
        <QRCode
            value={qrCodeData.qrCode}
            size={200}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            viewBox="0 0 256 256"
        />
    </div>
) : (
                                    <img src={Loadinggif} width={200} alt="Loading..." />
                                )}
                            </div>

                            {needsReload && (
                                <div className="bankid-reload-container">
                                    <button
                                        className="bankid-reload-btn"
                                        onClick={handleQRReload}
                                        disabled={qrState.isLoading}
                                    >
                                        <img src={ReloadIcon} alt="" />
                                        <span>{t('Reload QR')}</span>
                                    </button>
                                </div>
                            )}

                            {!qrState.isLoading && !needsReload && qrCodeData?.qrCode && (
                                <div className="bankid-qr-steps">
                                    <div className="bankid-qr-step">
                                        <span className="step-num">1</span>
                                        <span className="step-text">{t('QR_STEP_1')}</span>
                                    </div>
                                    <span className="step-divider" />
                                    <div className="bankid-qr-step">
                                        <span className="step-num">2</span>
                                        <span className="step-text">{t('QR_STEP_2')}</span>
                                    </div>
                                    <span className="step-divider" />
                                    <div className="bankid-qr-step">
                                        <span className="step-num">3</span>
                                        <span className="step-text">{t('QR_STEP_3')}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                    <>
                    {!hideOptions && (
                    <div className="bankid-options border-t-2 mt-4">
                        <button
                            className="bankid-option"
                            onClick={() => handleBankIDAuth('same-device')}
                           disabled={isInitializing || (autoInitialize && !canUseSameDevice) || toggleReactivate}
                        >
                            <div className="option-icon">
                                <img src={bankidcolor} alt="" />
                            </div>
                            <div className="option-content">
                                <h3>{t('OPEN_BANK_ID_ON_SAME_DEVICE')}</h3>
                                <div className="option-link">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={14}
                                        height={14}
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M12.5 4.33333C12.5 4.60948 12.7239 4.83333 13 4.83333C13.2761 4.83333 13.5 4.60948 13.5 4.33333H12.5ZM9.66667 0.5C9.39052 0.5 9.16667 0.723858 9.16667 1C9.16667 1.27614 9.39052 1.5 9.66667 1.5V0.5ZM6.64645 6.64645C6.45118 6.84171 6.45118 7.15829 6.64645 7.35355C6.84171 7.54882 7.15829 7.54882 7.35355 7.35355L6.64645 6.64645ZM12.9637 1.182L12.5182 1.40899L12.5182 1.409L12.9637 1.182ZM12.818 1.03633L12.591 1.48183V1.48184L12.818 1.03633ZM7 2.83333C7.27614 2.83333 7.5 2.60948 7.5 2.33333C7.5 2.05719 7.27614 1.83333 7 1.83333V2.83333ZM12.1667 7C12.1667 6.72386 11.9428 6.5 11.6667 6.5C11.3905 6.5 11.1667 6.72386 11.1667 7H12.1667ZM2.45603 12.7094L2.68302 12.2638L2.45603 12.7094ZM1.29065 11.544L0.845146 11.771L1.29065 11.544ZM10.2106 12.7094L9.98365 12.2638L10.2106 12.7094ZM11.376 11.544L10.9305 11.317L11.376 11.544ZM1.29065 3.78936L0.845146 3.56236H0.845146L1.29065 3.78936ZM2.45603 2.62398L2.22903 2.17848L2.22903 2.17848L2.45603 2.62398ZM13 4.33333H13.5V1.53333H13H12.5V4.33333H13ZM12.4667 1V0.5H9.66667V1V1.5H12.4667V1ZM12.9024 1.09763L12.5488 0.744078L6.64645 6.64645L7 7L7.35355 7.35355L13.2559 1.45118L12.9024 1.09763ZM13 1.53333H13.5C13.5 1.44824 13.5004 1.35435 13.4938 1.27368C13.4867 1.18718 13.4692 1.07289 13.4092 0.955008L12.9637 1.182L12.5182 1.409C12.4944 1.36242 12.4951 1.33045 12.4971 1.35512C12.4981 1.36715 12.499 1.38617 12.4995 1.41757C12.5 1.44895 12.5 1.48552 12.5 1.53333H13ZM12.4667 1V1.5C12.5145 1.5 12.5511 1.50001 12.5824 1.50051C12.6138 1.501 12.6329 1.5019 12.6449 1.50288C12.6695 1.5049 12.6376 1.50557 12.591 1.48183L12.818 1.03633L13.045 0.590827C12.9271 0.530762 12.8128 0.51327 12.7263 0.506202C12.6456 0.499611 12.5518 0.5 12.4667 0.5V1ZM12.9637 1.182L13.4092 0.955012C13.3692 0.876609 13.3174 0.80553 13.2559 0.744078L12.9024 1.09763L12.5488 1.45118C12.5365 1.4389 12.5262 1.42468 12.5182 1.40899L12.9637 1.182ZM12.9024 1.09763L13.2559 0.744078C13.1945 0.682625 13.1234 0.630774 13.045 0.590827L12.818 1.03633L12.591 1.48184C12.5753 1.47385 12.5611 1.46347 12.5488 1.45118L12.9024 1.09763ZM7.4 13V12.5H5.26667V13V13.5H7.4V13ZM1 8.73333H1.5V6.6H1H0.5V8.73333H1ZM5.26667 2.33333V2.83333H7V2.33333V1.83333H5.26667V2.33333ZM11.6667 7H11.1667V8.73333H11.6667H12.1667V7H11.6667ZM5.26667 13V12.5C4.51168 12.5 3.97551 12.4996 3.55592 12.4653C3.14217 12.4315 2.886 12.3673 2.68302 12.2638L2.45603 12.7094L2.22903 13.1549C2.59648 13.3421 2.9989 13.4232 3.47449 13.462C3.94425 13.5004 4.52818 13.5 5.26667 13.5V13ZM1 8.73333H0.5C0.5 9.47182 0.499611 10.0558 0.537992 10.5255C0.576849 11.0011 0.657921 11.4035 0.845146 11.771L1.29065 11.544L1.73615 11.317C1.63273 11.114 1.56848 10.8578 1.53467 10.4441C1.50039 10.0245 1.5 9.48832 1.5 8.73333H1ZM2.45603 12.7094L2.68302 12.2638C2.27534 12.0561 1.94388 11.7247 1.73615 11.317L1.29065 11.544L0.845146 11.771C1.14875 12.3668 1.63318 12.8513 2.22903 13.1549L2.45603 12.7094ZM7.4 13V13.5C8.13849 13.5 8.72242 13.5004 9.19218 13.462C9.66777 13.4232 10.0702 13.3421 10.4376 13.1549L10.2106 12.7094L9.98365 12.2638C9.78066 12.3673 9.5245 12.4315 9.11075 12.4653C8.69116 12.4996 8.15499 12.5 7.4 12.5V13ZM11.6667 8.73333H11.1667C11.1667 9.48832 11.1663 10.0245 11.132 10.4441C11.0982 10.8578 11.0339 11.114 10.9305 11.317L11.376 11.544L11.8215 11.771C12.0087 11.4035 12.0898 11.0011 12.1287 10.5255C12.1671 10.0558 12.1667 9.47182 12.1667 8.73333H11.6667ZM10.2106 12.7094L10.4376 13.1549C11.0335 12.8513 11.5179 12.3668 11.8215 11.771L11.376 11.544L10.9305 11.317C10.7228 11.7247 10.3913 12.0561 9.98365 12.2638L10.2106 12.7094ZM1 6.6H1.5C1.5 5.84501 1.50039 5.30884 1.53467 4.88925C1.56848 4.4755 1.63273 4.21934 1.73615 4.01635L1.29065 3.78936L0.845146 3.56236C0.657921 3.92981 0.576849 4.33223 0.537992 4.80782C0.499611 5.27758 0.5 5.86151 0.5 6.6H1ZM5.26667 2.33333V1.83333C4.52818 1.83333 3.94425 1.83294 3.47449 1.87133C2.9989 1.91018 2.59648 1.99125 2.22903 2.17848L2.45603 2.62398L2.68302 3.06949C2.886 2.96606 3.14217 2.90181 3.55592 2.868C3.97551 2.83372 4.51168 2.83333 5.26667 2.83333V2.33333ZM1.29065 3.78936L1.73615 4.01635C1.94388 3.60867 2.27534 3.27721 2.68302 3.06949L2.45603 2.62398L2.22903 2.17848C1.63318 2.48208 1.14875 2.96652 0.845146 3.56236L1.29065 3.78936Z"
                                            fill="black"
                                        />
                                    </svg>

                                </div>
                            </div>
                        </button>

                        <button
                            className="bankid-option"
                            onClick={() => handleBankIDAuth('qr-code')}
                            disabled={isInitializing || (autoInitialize && !canUseQRCode) || toggleReactivate}
                        >
                            <div className="option-icon">
                                <div className="qr-icon">
                                    <img src={bankidcolor} alt="" />
                                </div>
                            </div>
                            <div className="option-content">
                                <h3>{t('SCAN_QR_FROM_BANK_APP')}</h3>
                                <img src={Softwareqr} alt="" />
                            </div>
                        </button>
                    </div>
                    )}

                    {mode === 'signup' && (
                        <div className="bankid-info">
                            <p className="info-text">
                                {t('By signing up with BankID, your personal information will be automatically filled from your bank details.')}
                            </p>
                        </div>
                    )}
                    </>
                ))}

                    {!ssnMismatch && !nameMismatchType && footerContent && (
                        <div className="bankid-footer-content">
                            {footerContent}
                        </div>
                    )}

                </div>
                {/* Floating up / down scroll controls (scrollbar is hidden on this modal).
                    Only the direction(s) you can actually scroll are shown. */}
                {isLoginTheme && (scrollNav.up || scrollNav.down) && (
                    <div className="bankid-scroll-nav" aria-hidden="true">
                        <div className="bankid-scroll-nav-inner">
                            {scrollNav.up && (
                                <button
                                    type="button"
                                    className="bankid-scroll-btn"
                                    aria-label="Scroll up"
                                    onClick={() => scrollModalBy(-240)}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                            )}
                            {scrollNav.down && (
                                <button
                                    type="button"
                                    className="bankid-scroll-btn"
                                    aria-label="Scroll down"
                                    onClick={() => scrollModalBy(240)}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>


            <ReactivateModal
                show={toggleReactivate}
                onClose={handleReactivationClose}
                onSuccess={handleReactivationSuccess}
                email={userEmail}
                qrCodeEmail={userEmail}
                inactiveAccount={inactiveAccount}
                mode={mode}
            />
        </div>
    );
};

export default BankIDComponent;
